/** The routing page's reducers: what can be modified, when a resume may go, which limits can be raised (U042-U048). */
import type { HaltFacts, LimitRow, OrderRow, RoutingFacts } from "../../contracts/account.ts";
import { instantFromMicros } from "../../app/command-dialog.ts";
import { parseInstant } from "../../app/format.ts";

const MODIFIABLE = new Set(["ACKNOWLEDGED", "WORKING", "PARTIALLY_FILLED"]);

/** Whether the broker can change the order now: live (not in flight or unknown) and not a protective stop. */
export function isModifiable(row: OrderRow): boolean {
  return MODIFIABLE.has(row.state) && row.role !== "PROTECTIVE_STOP";
}

export interface ModificationUse {
  readonly used: number;
  readonly cap: number;
  /** The order has used all of its allowed modifications. */
  readonly capped: boolean;
}

/** An order's modifications against the cap; an order the server never counted has used none. */
export function modificationUse(facts: RoutingFacts, orderId: string): ModificationUse {
  const used = facts.modifications.find((count) => count.orderId === orderId)?.used ?? 0;
  return { used, cap: facts.modificationCap, capped: used >= facts.modificationCap };
}

/** When a halt's cooldown ends: the halt's start plus the cooldown the server reports. */
export function cooldownEnd(halt: HaltFacts): string {
  return instantFromMicros(
    parseInstant(halt.haltedSince) + BigInt(halt.cooldownSeconds) * 1_000_000n,
  );
}

/** A limit that has an amount. */
export type Raisable = LimitRow & { readonly limitPaise: bigint };

/** The limits that carry an amount a person could raise (a deadline has none). */
export function raisableLimits(limits: readonly LimitRow[]): readonly Raisable[] {
  return limits.filter((limit): limit is Raisable => limit.limitPaise !== null);
}
